import React, { useMemo } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Checkbox,
  FormInstance,
  Divider,
  Image,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import { MemberTypeFormData, PaymentType, PeriodType, MemberLevel } from '../types';
import { FORM_DEFAULT_VALUES, QUERY_KEYS } from '../constants';
import { getPrivilegeList } from '../api';
import UploadImage from '@/components/UploadImage';
import styles from './MemberTypeForm.module.less';

interface MemberTypeFormProps {
  form: FormInstance<MemberTypeFormData>;
}

const MemberTypeForm: React.FC<MemberTypeFormProps> = ({ form }) => {
  // 获取权益列表
  const { data: privilegeList, isLoading: isLoadingPrivileges } = useQuery({
    queryKey: [QUERY_KEYS.PRIVILEGE_LIST],
    queryFn: getPrivilegeList,
  });

  // 权益选项
  const privilegeOptions = useMemo(() => {
    return privilegeList || [];
  }, [privilegeList]);

  // // 监听原价和现价变化，自动计算折扣
  // const originalAmount = Form.useWatch('originalAmount', form);
  // const amount = Form.useWatch('amount', form);

  // useEffect(() => {
  //   if (originalAmount && amount && originalAmount > 0 && amount > 0) {
  //     // 计算折扣百分比：(1 - 现价/原价) * 100
  //     const discountPercent = ((1 - amount / originalAmount) * 100).toFixed(2);
  //     form.setFieldValue('discount', `${discountPercent}%`);
  //   } else {
  //     // 如果原价或现价为空，清空折扣
  //     form.setFieldValue('discount', '');
  //   }
  // }, [originalAmount, amount, form]);

  return (
    <div className={styles.memberTypeForm}>
      <Form form={form} layout="vertical" initialValues={FORM_DEFAULT_VALUES}>
      <Form.Item
        name="type"
        label="类型"
        rules={[{ required: true, message: '请选择类型' }]}
      >
        <Select placeholder="请选择类型">
          <Select.Option value={PaymentType.APPLE_PAY}>Apple Pay</Select.Option>
          <Select.Option value={PaymentType.GOOGLE_PAY}>Google Pay</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        name="level"
        label="会员等级"
        rules={[{ required: true, message: '请选择会员等级' }]}
      >
        <Radio.Group>
          <Radio value={MemberLevel.STANDARD}>Pro</Radio>
          <Radio value={MemberLevel.PREMIUM}>Premium</Radio>
          <Radio value={MemberLevel.BASIC}>Basic</Radio>
          <Radio value={MemberLevel.LITE}>Lite</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        name="privilegeIds"
        label="会员权益"
        rules={[{ required: true, message: '请选择会员权益' }]}
      >
        <Checkbox.Group style={{ width: '100%' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            {isLoadingPrivileges ? (
              <span>加载中...</span>
            ) : privilegeOptions.length === 0 ? (
              <span>暂无权益数据</span>
            ) : (
              privilegeOptions.map((privilege) => (
                <Checkbox key={privilege.id} value={privilege.id}>
                  <div style={{display: "flex"}}>
                    <Image 
                      src={privilege.iconUrl} 
                      alt="" 
                      width={20} 
                      height={20} 
                      // style={{marginRight: 5, objectFit: 'contain'}}
                      preview={{ mask: 'see' }}
                    />
                    <div style={{marginLeft: 5}}>
                      {privilege.titleEn || '-'}
                      {/* {privilege.titleJa && `（${privilege.titleJa}）`} */}
                    </div>
                  </div>
                </Checkbox>
              ))
            )}
          </div>
        </Checkbox.Group>
      </Form.Item>

      <Form.Item
        name="originalAmount"
        label="原价"
        rules={[
          { required: true, message: '请输入原价' },
          {
            validator: (_, value) => {
              const amount = form.getFieldValue('amount');
              if (amount !== undefined && amount !== null && amount !== '') {
                if (Number(value) < Number(amount)) {
                  return Promise.reject(new Error('原价必须大于等于现价'));
                }
              }
              return Promise.resolve();
            },
          },
        ]}
        dependencies={['amount']}
      >
        <InputNumber
          placeholder="请输入原价"
          min={0}
          precision={2}
          style={{ width: '100%' }}
        />
      </Form.Item>

      <Form.Item
        name="amount"
        label="现价"
        rules={[{ required: true, message: '请输入现价' }]}
      >
        <InputNumber
          placeholder="请输入现价"
          min={0}
          precision={2}
          style={{ width: '100%' }}
        />
      </Form.Item>

      <Form.Item
        name="discount"
        label="折扣"
        rules={[{ required: true, message: '请输入折扣' }]}
      >
        <Input placeholder="请输入折扣" maxLength={50} />
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) =>
          prevValues.discount !== currentValues.discount
        }
      >
        {({ getFieldValue }) => {
          const discount = getFieldValue('discount');
          // 将折扣字符串转换为数字，处理百分比和纯数字两种情况
          let discountNum: number | null = null;
          if (discount) {
            // 移除百分号并转换为数字
            const cleanedDiscount = String(discount).replace('%', '').trim();
            discountNum = parseFloat(cleanedDiscount);
            // 如果无法解析为有效数字，设为null
            if (isNaN(discountNum)) {
              discountNum = null;
            } else {
              // 如果是百分比格式（如50%），转换为小数（0.5）
              if (String(discount).includes('%')) {
                discountNum = discountNum / 100;
              }
            }
          }
          // 当折扣小于1时，要求必填
          const isDiscountRequired = discountNum !== null && !isNaN(discountNum) && discountNum < 1;

          return (
            <>
              <Form.Item
                name="discountDescEn"
                label="折扣描述-英文"
                rules={
                  isDiscountRequired
                    ? [{ required: true, message: '请输入折扣描述（英文）' }]
                    : []
                }
                dependencies={['discount']}
              >
                <Input.TextArea
                  placeholder="请输入折扣描述，七天免费描述（英文）"
                  maxLength={500}
                  rows={4}
                  showCount
                />
              </Form.Item>

              <Form.Item
                name="discountDescJa"
                label="折扣描述-日文"
                rules={
                  isDiscountRequired
                    ? [{ required: true, message: '请输入折扣描述（日文）' }]
                    : []
                }
                dependencies={['discount']}
              >
                <Input.TextArea
                  placeholder="请输入折扣描述，七天免费描述（日文）"
                  maxLength={500}
                  rows={4}
                  showCount
                />
              </Form.Item>
            </>
          );
        }}
      </Form.Item>

      <Form.Item
        name="periodType"
        label="产品类型"
        rules={[{ required: true, message: '请选择产品类型' }]}
      >
        <Radio.Group>
          <Radio value={PeriodType.MONTH}>月</Radio>
          <Radio value={PeriodType.HALF_YEAR}>半年</Radio>
          <Radio value={PeriodType.YEAR}>年</Radio>
          <Radio value={PeriodType.QUARTER}>季度</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        name="productId"
        label="商品id"
        rules={[{ required: true, message: '请输入商品id' }]}
      >
        <Input placeholder="请输入商品id" maxLength={100} />
      </Form.Item>

      <Form.Item
        name="status"
        label="启用状态"
        rules={[{ required: true, message: '请选择启用状态' }]}
      >
        <Radio.Group>
          <Radio value={0}>禁用</Radio>
          <Radio value={1}>启用</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        name="limitStatus"
        label="是否期间限定"
        rules={[{ required: true, message: '请选择是否期间限定' }]}
      >
        <Radio.Group>
          <Radio value={0}>否</Radio>
          <Radio value={1}>是</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) =>
          prevValues.limitStatus !== currentValues.limitStatus
        }
      >
        {({ getFieldValue }) => {
          const limitStatus = getFieldValue('limitStatus');
          const isLimitRequired = limitStatus === 1;

          return (
            <>
              <Form.Item
                name="limitIconEn"
                label="期间限定图标-英文"
                rules={
                  isLimitRequired
                    ? [{ required: true, message: '请上传期间限定图标（英文）' }]
                    : []
                }
                dependencies={['limitStatus']}
              >
                <UploadImage
                  width={97}
                  height={35}
                  showSizeHint={true}
                  fileTypes={['image/png', 'image/jpeg', 'image/jpg', 'image/webp']}
                />
              </Form.Item>

              <Form.Item
                name="limitIconJa"
                label="期间限定图标-日文"
                rules={
                  isLimitRequired
                    ? [{ required: true, message: '请上传期间限定图标（日文）' }]
                    : []
                }
                dependencies={['limitStatus']}
              >
                <UploadImage
                  width={97}
                  height={35}
                  showSizeHint={true}
                  fileTypes={['image/png', 'image/jpeg', 'image/jpg', 'image/webp']}
                />
              </Form.Item>

              <Form.Item
                name="recommendStatus"
                label="是否推荐"
                rules={[{ required: true, message: '请选择是否推荐' }]}
              >
                <Radio.Group>
                  <Radio value={0}>否</Radio>
                  <Radio value={1}>是</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                name="recommendIconEn"
                label="推荐图标-英文"
              >
                <UploadImage
                  width={97}
                  height={35}
                  showSizeHint={true}
                  fileTypes={['image/png', 'image/jpeg', 'image/jpg', 'image/webp']}
                />
              </Form.Item>

              <Form.Item
                name="recommendIconJa"
                label="推荐图标-日文"
              >
                <UploadImage
                  width={97}
                  height={35}
                  showSizeHint={true}
                  fileTypes={['image/png', 'image/jpeg', 'image/jpg', 'image/webp']}
                />
              </Form.Item>
            </>
          );
        }}
      </Form.Item>

      <Divider orientation="left">多语言信息</Divider>

      <Form.Item
        name="memberNameEn"
        label="会员名称-英文"
      >
        <Input placeholder="请输入会员名称（英文）" maxLength={200} />
      </Form.Item>

      <Form.Item
        name="memberNameJa"
        label="会员名称-日语"
      >
        <Input placeholder="请输入会员名称（日语）" maxLength={200} />
      </Form.Item>

      <Form.Item
        name="memberDescEn"
        label="描述-英文"
      >
        <Input.TextArea 
          placeholder="请输入描述（英文）" 
          maxLength={500} 
          rows={4}
          showCount
        />
      </Form.Item>

      <Form.Item
        name="memberDescJa"
        label="描述-日语"
      >
        <Input.TextArea 
          placeholder="请输入描述（日语）" 
          maxLength={500} 
          rows={4}
          showCount
        />
      </Form.Item>

      <Form.Item
        name="amountDescEn"
        label="金额描述-英文"
      >
        <Input.TextArea 
          placeholder="请输入金额描述（英文）" 
          maxLength={500} 
          rows={4}
          showCount
        />
      </Form.Item>

      <Form.Item
        name="amountDescJa"
        label="金额描述-日语"
      >
        <Input.TextArea 
          placeholder="请输入金额描述（日语）" 
          maxLength={500} 
          rows={4}
          showCount
        />
      </Form.Item>

      <Form.Item
        name="activityDescEn"
        label="活动描述-英文"
      >
        <Input.TextArea 
          placeholder="请输入活动描述（英文）" 
          maxLength={500} 
          rows={4}
          showCount
        />
      </Form.Item>

      <Form.Item
        name="activityDescJa"
        label="活动描述-日语"
      >
        <Input.TextArea 
          placeholder="请输入活动描述（日语）" 
          maxLength={500} 
          rows={4}
          showCount
        />
      </Form.Item>
    </Form>
    </div>
  );
};

export default MemberTypeForm;

